import { useState, useRef, useEffect, useMemo } from "react";

/**
 * SearchableSelect — dropdown ที่พิมพ์ค้นหาได้
 * ใช้แทน <select> ที่ตัวเลือกเยอะจนเลื่อนหายาก (เช่น 77 จังหวัด)
 *
 * props:
 *   value        — ค่าปัจจุบัน (string)
 *   onChange(v)  — เรียกเมื่อเลือก/ล้างค่า
 *   options      — ["ก","ข"] หรือ [{value,label}]
 *   placeholder  — ข้อความตอนยังไม่เลือก
 *   inputStyle   — style เพิ่มเติมของช่อง input (หน้าที่ใช้ inline style ส่งมาได้)
 *   inputClassName — ใส่ class ให้ input (หน้าที่ใช้ CSS class ส่งมาได้)
 *   disabled
 *
 * เลือกได้เฉพาะจากรายการเท่านั้น (พิมพ์มั่วแล้วปิด = กลับเป็นค่าเดิม)
 * เพราะค่านี้ถูกเอาไป group ในรายงาน — ถ้าปล่อยพิมพ์อิสระจะได้ "เชียงใหม่" กับ "เชียงไหม่" แยกกัน
 */
export default function SearchableSelect({
  value = "",
  onChange,
  options = [],
  placeholder = "",
  inputStyle,
  inputClassName,
  disabled = false,
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi]       = useState(0);      // index ที่กำลังไฮไลต์
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const items = useMemo(
    () => options.map(o => (typeof o === "string" ? { value: o, label: o } : o)),
    [options]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(o => o.label.toLowerCase().includes(q));
  }, [items, query]);

  const selectedLabel = items.find(o => o.value === value)?.label ?? value ?? "";

  /* ปิดเมื่อคลิกนอกกรอบ — ทิ้งคำค้นที่พิมพ์ค้างไว้ */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  /* เลื่อนรายการที่ไฮไลต์ให้อยู่ในกรอบเสมอ (ตอนกดลูกศร) */
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[hi];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [hi, open]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    // เริ่มไฮไลต์ที่ค่าที่เลือกอยู่ จะได้เห็นว่าตอนนี้คืออันไหน
    const idx = items.findIndex(o => o.value === value);
    setHi(idx >= 0 ? idx : 0);
  };

  const pick = (opt) => {
    onChange?.(opt.value);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (["ArrowDown", "Enter"].includes(e.key)) { e.preventDefault(); openList(); }
      return;
    }
    if (e.key === "ArrowDown")      { e.preventDefault(); setHi(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter")     { e.preventDefault(); if (filtered[hi]) pick(filtered[hi]); }
    else if (e.key === "Escape")    { e.preventDefault(); setOpen(false); setQuery(""); }
    else if (e.key === "Tab")       { setOpen(false); setQuery(""); }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        className={inputClassName}
        // paddingRight เผื่อที่ให้ไอคอนเสมอ ไม่ว่าหน้าที่เรียกจะส่ง style อะไรมา
        style={{ width: "100%", boxSizing: "border-box", ...inputStyle, paddingRight: 32 }}
        disabled={disabled}
        placeholder={placeholder}
        value={open ? query : selectedLabel}
        onFocus={openList}
        onClick={openList}
        onChange={(e) => { setQuery(e.target.value); setHi(0); if (!open) setOpen(true); }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />

      {/* ลูกศร / ปุ่มล้างค่า */}
      <span
        onMouseDown={(e) => {
          e.preventDefault();
          if (value && !open) { onChange?.(""); return; }   // มีค่าแล้ว = กากบาทล้างค่า
          if (open) { setOpen(false); setQuery(""); }
          else inputRef.current?.focus();
        }}
        style={{
          position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
          color: "#9ca3af", cursor: disabled ? "default" : "pointer",
          display: "flex", alignItems: "center", pointerEvents: disabled ? "none" : "auto",
        }}
        title={value && !open ? "ล้างค่า" : undefined}
      >
        {value && !open ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }}>
            <path fill="currentColor" d="M6 8L1 3h10z" />
          </svg>
        )}
      </span>

      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 3000,
            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 9,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)", overflow: "hidden",
          }}
        >
          <div ref={listRef} style={{ maxHeight: 240, overflowY: "auto", padding: "4px 0" }}>
            {filtered.length === 0 && (
              <div style={{ padding: "12px 14px", fontSize: 12.5, color: "#9ca3af", textAlign: "center" }}>
                ไม่พบ “{query}”
              </div>
            )}
            {filtered.map((o, i) => (
              <div
                key={o.value}
                onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                onMouseEnter={() => setHi(i)}
                style={{
                  padding: "8px 14px", fontSize: 13.5, cursor: "pointer",
                  fontFamily: "'Noto Sans Thai',sans-serif",
                  background: i === hi ? "#eff6ff" : "transparent",
                  color: o.value === value ? "#1d4ed8" : "#374151",
                  fontWeight: o.value === value ? 600 : 400,
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                }}
              >
                <span>{o.label}</span>
                {o.value === value && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
